import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { ExamSchedule } from '@/types';
import { formatDate, formatDday } from '@/lib/datetime';
import Field, { inputClass } from '@/components/base/Field';

interface ExamScheduleSectionProps {
  studentId: string;
}

interface Draft {
  title: string;
  startDate: string;
  endDate: string;
  scope: string;
}

const emptyDraft: Draft = {
  title: '',
  startDate: '',
  endDate: '',
  scope: '',
};

const toneClass: Record<'past' | 'soon' | 'far', string> = {
  past: 'bg-background-200 text-foreground-500',
  soon: 'bg-accent-500 text-background-50',
  far: 'bg-primary-100 text-primary-700',
};

export default function ExamScheduleSection({ studentId }: ExamScheduleSectionProps) {
  const [schedules, setSchedules] = useState<ExamSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('exam_schedules')
        .select('*')
        .eq('student_id', studentId)
        .order('start_date', { ascending: true });
      if (fetchError) throw fetchError;
      setSchedules((data as ExamSchedule[]) ?? []);
    } catch {
      setError('시험 일정을 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    load();
  }, [load]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.title.trim()) {
      setError('시험명을 입력해주세요.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const { data, error: insertError } = await supabase
        .from('exam_schedules')
        .insert({
          student_id: studentId,
          title: draft.title.trim(),
          start_date: draft.startDate || null,
          end_date: draft.endDate || null,
          scope: draft.scope.trim(),
        })
        .select();
      if (insertError) throw insertError;
      const created = (data as ExamSchedule[] | null)?.[0];
      if (created) {
        setSchedules((prev) =>
          [...prev, created].sort((a, b) => (a.start_date ?? '').localeCompare(b.start_date ?? '')),
        );
      }
      setDraft(emptyDraft);
      setOpen(false);
    } catch {
      setError('시험 일정 저장에 실패했어요.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    try {
      const { error: deleteError } = await supabase.from('exam_schedules').delete().eq('id', id);
      if (deleteError) throw deleteError;
      setSchedules((prev) => prev.filter((s) => s.id !== id));
    } catch {
      setError('삭제에 실패했어요.');
    }
  };

  return (
    <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
            <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className="ri-calendar-event-line"></i>
            </span>
            시험 일정
          </h2>
          <p className="mt-1 text-xs text-foreground-500">
            시험 기간과 시험 범위를 미리 기록하고 다가오는 시험을 한눈에 확인해요.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-1.5 rounded-md bg-primary-500 px-3.5 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 whitespace-nowrap"
        >
          <i className={open ? 'ri-close-line' : 'ri-add-line'}></i>
          {open ? '닫기' : '일정 추가'}
        </button>
      </div>

      {open && (
        <form
          onSubmit={handleAdd}
          className="mt-5 grid grid-cols-1 gap-4 rounded-lg border border-background-200 bg-background-100 p-4 sm:grid-cols-2"
        >
          <div className="sm:col-span-2">
            <Field label="시험명" required>
              <input
                type="text"
                value={draft.title}
                onChange={(e) => set('title', e.target.value)}
                placeholder="예: 2학기 중간고사"
                className={inputClass}
              />
            </Field>
          </div>
          <Field label="시작일">
            <input
              type="date"
              value={draft.startDate}
              onChange={(e) => set('startDate', e.target.value)}
              className={inputClass}
            />
          </Field>
          <Field label="종료일">
            <input
              type="date"
              value={draft.endDate}
              onChange={(e) => set('endDate', e.target.value)}
              className={inputClass}
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="시험 범위">
              <textarea
                value={draft.scope}
                onChange={(e) => set('scope', e.target.value)}
                maxLength={500}
                rows={3}
                placeholder="예: 공통수학1 - 다항식 ~ 이차방정식"
                className={`${inputClass} resize-none`}
              />
            </Field>
          </div>
          <div className="flex items-end sm:col-span-2">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
              일정 저장
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-3 py-2 text-xs text-accent-800">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-10 text-sm text-foreground-500">
          <i className="ri-loader-4-line mr-2 animate-spin"></i>
          불러오는 중...
        </div>
      ) : schedules.length === 0 ? (
        <p className="mt-5 rounded-lg border border-dashed border-background-300 py-10 text-center text-sm text-foreground-500">
          아직 등록된 시험 일정이 없어요.
        </p>
      ) : (
        <div className="mt-5 flex flex-col gap-3">
          {schedules.map((s) => {
            const dday = formatDday(s.end_date ?? s.start_date);
            return (
              <div
                key={s.id}
                className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-sm font-semibold text-foreground-950">{s.title}</p>
                    {dday && (
                      <span
                        className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${toneClass[dday.tone]}`}
                      >
                        {dday.label}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 flex items-center gap-1 text-xs text-foreground-500">
                    <i className="ri-calendar-line"></i>
                    {formatDate(s.start_date)} ~ {formatDate(s.end_date)}
                  </p>
                  {s.scope && (
                    <p className="mt-1.5 text-xs leading-relaxed text-foreground-600">
                      <span className="font-medium text-foreground-700">범위 </span>
                      {s.scope}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => remove(s.id)}
                  aria-label="삭제"
                  className="flex h-8 w-8 shrink-0 items-center justify-center self-end rounded-md text-foreground-400 transition hover:bg-background-200 hover:text-accent-700 sm:self-start"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}